import React, { useState } from "react";
import { PivotRow, PivotTableResult } from "./pivotUtils";
import { DataTable, DataTableRowToggleEvent } from "primereact/datatable";
import { Column, ColumnSortEvent } from "primereact/column";
import { formatExactMoneyDollars } from "../Money";
import { formatSicDisplay } from "./sic";
import styles from "./PivotTable.module.css";

const tivBody = (r: PivotRow) =>
  r.total_tiv != null ? (
    <div>
      {formatExactMoneyDollars(r.total_tiv, {
        withRounding: true,
        withDollarSign: true,
      })}
    </div>
  ) : null;

const formatPct = (n: number) => `${Math.round(n * 100) / 100}%`;

const pctBody = (r: PivotRow) =>
  r.percent_of_tiv != null ? formatPct(r.percent_of_tiv) : "";

export const PivotTable: React.FC<{
  pivotKey: string;
  pivotData: PivotTableResult;
  label: string;
  compare?: (a: string, b: string) => number;
}> = ({ pivotKey, pivotData, label, compare }) => {
  const [expandedRows, setExpandedRows] = useState<Record<
    string,
    boolean
  > | null>(null);
  const total = pivotData.total_row;
  const hasSubRows = pivotData.rows.some(
    (r) => r.sub_rows && r.sub_rows.length > 0
  );
  const hasOuterValues = pivotData.rows.some(
    (r) => r.location_count != null || r.total_tiv != null
  );

  const groupValueBody = (r: PivotRow) =>
    pivotKey === "sic_code" ? formatSicDisplay(r.group_value) : r.group_value;

  const groupValueSort = compare
    ? (e: ColumnSortEvent) => {
        const dir = e.order ?? 1;
        return [...(e.data as PivotRow[])].sort(
          (a, b) => dir * compare(a.group_value, b.group_value)
        );
      }
    : undefined;

  const rowExpansionTemplate = (row: PivotRow) => {
    if (!row.sub_rows || row.sub_rows.length === 0) return null;
    return (
      <DataTable value={row.sub_rows} size="small">
        <Column
          field="group_value"
          header={pivotData.sub_column_name ?? "Detail"}
        />
        <Column field="location_count" header="Count" />
        <Column field="total_tiv" header="TIV" body={tivBody} />
        <Column
          field="percent_of_tiv"
          header={pivotData.sub_percent_label ?? "% of Group TIV"}
          body={pctBody}
        />
      </DataTable>
    );
  };

  return (
    <div className={styles.pivotCard}>
      <h4 className={styles.pivotCardTitle}>{label}</h4>
      <div className={styles.pivotTableWrap}>
        <DataTable
          stripedRows
          value={pivotData.rows}
          sortMode="multiple"
          dataKey="group_value"
          expandedRows={expandedRows ?? undefined}
          onRowToggle={(e: DataTableRowToggleEvent) =>
            setExpandedRows(e.data as Record<string, boolean>)
          }
          rowExpansionTemplate={hasSubRows ? rowExpansionTemplate : undefined}
        >
          {hasSubRows && <Column expander style={{ width: "3rem" }} />}
          <Column
            field="group_value"
            header={label}
            sortable
            sortFunction={groupValueSort}
            body={groupValueBody}
            footer={hasOuterValues ? total?.group_value : undefined}
          />
          {hasOuterValues && (
            <Column
              field="location_count"
              header="Count"
              sortable
              footer={
                total?.location_count != null
                  ? String(total.location_count)
                  : ""
              }
            />
          )}
          {hasOuterValues && (
            <Column
              field="total_tiv"
              header="TIV"
              sortable
              body={tivBody}
              footer={
                total?.total_tiv != null
                  ? formatExactMoneyDollars(total.total_tiv, {
                      withRounding: true,
                      withDollarSign: true,
                    })
                  : ""
              }
            />
          )}
          {hasOuterValues && (
            <Column
              field="percent_of_tiv"
              header="% of TIV"
              body={pctBody}
              sortable
              footer={
                total?.percent_of_tiv != null
                  ? formatPct(total.percent_of_tiv)
                  : ""
              }
            />
          )}
        </DataTable>
      </div>
    </div>
  );
};
